import {isFilled} from '@prismicio/helpers';
import type {LinkField, PrismicDocument} from '@prismicio/types';
import {useLinkHelper, useLocalePrefix} from '#imports';
import type {FilledContent} from '~~/app/composables/contentHelpers';
import {createDateHelpers} from '~~/app/composables/dateHelpers';

export type ContentRedirect = {
    to: string;
    target?: '_blank';
};

type ResourceContentDoc = FilledContent | PrismicDocument;

export function useResourceContentRedirect() {
    const {getLink, resolvePrismicLink} = useLinkHelper();
    const {withLocalePrefix} = useLocalePrefix();
    const {isBefore, isSameDay} = createDateHelpers();

    function normalizeTo(link: string): string {
        return link.startsWith('/') ? withLocalePrefix(link) : link;
    }

    function uidRedirect(doc: ResourceContentDoc): ContentRedirect {
        return {to: normalizeTo(resolvePrismicLink(doc))};
    }

    function redirectFromLinkField(field: LinkField | null | undefined): ContentRedirect | null {
        if (!isFilled.link(field)) {
            return null;
        }
        const resolved = getLink(field);
        if (!resolved?.url) {
            return null;
        }
        return {
            to: normalizeTo(resolved.url),
            target: resolved.target === '_blank' ? '_blank' : undefined,
        };
    }

    function pressReleaseRedirect(doc: ResourceContentDoc): ContentRedirect {
        if (!doc.data || !('externalLink' in doc.data)) {
            return uidRedirect(doc);
        }
        const field = doc.data.externalLink;
        if (!isFilled.link(field) || field.link_type !== 'Web') {
            return uidRedirect(doc);
        }
        return redirectFromLinkField(field) ?? uidRedirect(doc);
    }

    function isUpcomingEvent(doc: ResourceContentDoc): boolean {
        const date = doc.data && 'date' in doc.data ? doc.data.date : undefined;
        if (typeof date !== 'string') {
            return false;
        }
        const eventDate = new Date(date);
        const now = new Date();
        return isBefore(now, eventDate) || isSameDay(now, eventDate);
    }

    function eventLinkField(doc: ResourceContentDoc): LinkField | null | undefined {
        if (!doc.data) {
            return undefined;
        }
        if (isUpcomingEvent(doc)) {
            return 'signupLink' in doc.data ? doc.data.signupLink : undefined;
        }
        return 'replayLink' in doc.data ? doc.data.replayLink : undefined;
    }

    function eventRedirect(doc: ResourceContentDoc): ContentRedirect {
        return redirectFromLinkField(eventLinkField(doc)) ?? uidRedirect(doc);
    }

    function resolveContentRedirect(doc: ResourceContentDoc): ContentRedirect {
        if (doc.type === 'pressRelease') {
            return pressReleaseRedirect(doc);
        }
        if (doc.type === 'event') {
            return eventRedirect(doc);
        }
        return uidRedirect(doc);
    }

    return {resolveContentRedirect};
}
